import React, { useMemo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { appActions } from 'src/store/actions';
import { GridWrapper, Button } from 'src/components';
import { Page, DeleteClient } from 'src/containers';
import { Container, Col, Icon, Row, Text } from '@pinpoint/design-kit';
import CONSTANTS from 'src/store/constants';
import { fetchClientSftpList } from 'src/store/api';
import { ROUTES } from 'src/config';
import { CellDate, CellText } from 'src/components/cells';

function Clients({
  history,
  profile: { siteRole },
  clients,
  fetchClients,
  fetchClientsStatus,
  deleteClient,
  deleteClientStatus,
  deleteClientError,
  enqueueNotification,
}) {
  const [sftpEnabled, setSftpEnabled] = useState({});

  useEffect(() => {
    let active = true;
    fetchClientSftpList()
      .then((data) => {
        if (!active) return;
        const enabledByTenant = {};
        (data.items || []).forEach((item) => {
          enabledByTenant[item.tenant] = !!item.enabled;
        });
        setSftpEnabled(enabledByTenant);
      })
      .catch(() => {
        if (active) setSftpEnabled({});
      });
    return () => {
      active = false;
    };
  }, []);

  /* eslint-disable react/prop-types */
  // TODO: Add conditional logic to add the Tenant ID column only for pinpoint super admins
  const columns = useMemo(() => {
    const cols = [
      {
        Header: (
          <Text textSize="small" p={{ x: '3em' }}>
            Client Name
          </Text>
        ),
        id: 'name',
        accessor: 'name',
        justify: 'left',
        Cell: ({ value }) => CellText({ value, p: { x: '3em' } }),
      },
      {
        Header: <Text textSize="small">Contract End</Text>,
        id: 'contractEndDate',
        accessor: 'contractEndDate',
        justify: 'left',
        Cell: CellDate,
      },
      {
        Header: <Text textSize="small">Client Deadline</Text>,
        id: 'contractNoticeDate',
        accessor: 'contractNoticeDate',
        justify: 'left',
        Cell: CellDate,
      },
      {
        Header: <Text textSize="small">Model</Text>,
        id: 'canModel',
        accessor: 'canModel',
        justify: 'left',
        sortType: 'basic',
        Cell: CellToggle,
      },
      {
        Header: <Text textSize="small">Predict</Text>,
        id: 'canScore',
        accessor: 'canScore',
        justify: 'left',
        sortType: 'basic',
        Cell: CellToggle,
      },
      {
        Header: <Text textSize="small">RT API</Text>,
        id: 'isRealTimeApiEnabled',
        accessor: 'isRealTimeApiEnabled',
        justify: 'left',
        sortType: 'basic',
        Cell: CellToggle,
      },
      {
        Header: <Text textSize="small">SFTP</Text>,
        id: 'sftpEnabled',
        accessor: (row) => !!sftpEnabled[row.tenant],
        justify: 'left',
        sortType: 'basic',
        Cell: CellToggle,
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        justify: 'left',
        id: 'actions',
        accessor: ({ id, tenant }) => CellIcon({ tenant, id, history, deleteClient }),
        Cell: CellText,
      },
    ];
    if (siteRole === CONSTANTS.ROLE.SUPER_ADMIN) {
      cols.splice(0, 0, {
        Header: (
          <Text textSize="small" p={{ l: '3em' }}>
            ID
          </Text>
        ),
        id: 'tenant',
        accessor: 'tenant',
        justify: 'left',
        sortType: 'basic',
        Cell: ({ value }) => CellText({ value, p: { l: '3em' }, copy: true }),
      });
    }
    return cols;
  }, [history, deleteClient, siteRole, sftpEnabled]);
  /* eslint-enable react/prop-types */

  const getData = useCallback(() => {
    fetchClients();
  }, [fetchClients]);

  const hasData = clients && clients.length > 0;

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (deleteClientStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(deleteClientError);
    }
    if (deleteClientStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Client successfully deleted', { autoClose: true });
    }
  }, [deleteClientStatus, deleteClientError, enqueueNotification]);

  return (
    <Container w="100%" minW="100%">
      <Row d="flex" justify="flex-end">
        <Col>
          <Button
            buttonStyle="primary"
            m={{ t: '1rem' }}
            onClick={() => {
              history.push('/clients/add');
            }}
            data-tip="Edit Client information, or activate Real Time API access."
          >
            <Icon color="white" name="Plus" m={{ r: '.5em' }} />
            <Text color="white">ADD</Text>
          </Button>
        </Col>
      </Row>
      <Row>
        <Col size="12">
          {hasData && (
            <Text
              textSize="subtitle"
              textWeight="500"
              textColor="navyBlue"
              p={{ l: '3.25rem', t: '2.75rem' }}
              pos="absolute"
            >
              Clients
            </Text>
          )}
          <GridWrapper
            hasData={hasData}
            getData={getData}
            columns={columns}
            data={clients}
            apiStatus={fetchClientsStatus}
            hasGlobalFilter
            paginate
            gridState={{
              pageSize: 20,
            }}
            widths={
              siteRole === CONSTANTS.ROLE.SUPER_ADMIN
                ? '2fr 2fr 1fr 1fr .8fr .8fr .8fr .8fr 1fr'
                : '2fr 1fr 1fr .8fr .8fr .8fr .8fr 1fr'
            }
          />
        </Col>
      </Row>
    </Container>
  );
}

const mapStateToProps = ({ app }, ownProps) => ({ ...app, ...ownProps });

const mapDispatchToProps = (dispatch) => ({
  fetchClients: () => dispatch(appActions.fetchClients()),
  deleteClient: (id) => dispatch(appActions.delClient(id)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

Clients.propTypes = {
  profile: PropTypes.shape({
    siteRole: PropTypes.string.isRequired,
  }).isRequired,
  clients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchClients: PropTypes.func.isRequired,
  fetchClientsStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
  deleteClient: PropTypes.func.isRequired,
  deleteClientError: PropTypes.string.isRequired,
  deleteClientStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

// eslint-disable-next-line react/prop-types
function CellIcon({ tenant, deleteClient, history, id }) {
  return (
    <>
      <Icon
        cursor="pointer"
        data-tip="Edit client"
        hoverTransform="scale(1.25)"
        m={{ r: '.3125rem' }}
        name="Edit"
        size=".75rem"
        viewBox="0 0 50 50"
        onClick={() => history.push(ROUTES.CLIENTS_EDIT.replace(':clientId', id))}
      />
      <DeleteClient iconSize=".75rem" tenant={tenant} onDelete={deleteClient} />
    </>
  );
}

function CellToggle({ value }) {
  return (
    <Text textSize="small" m={{ r: '.5rem' }}>
      {value ? 'On' : 'Off'}
    </Text>
  );
}

CellToggle.propTypes = {
  value: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Clients));
